import {useState, useEffect, useMemo} from "react";
import {useQuery, useMutation} from "@tanstack/react-query";
import {useLocation} from "wouter";
import {queryClient} from "@/lib/queryClient";
import {type MastermindSpace} from "@shared/schema";
import {mastermindSpacesService} from "@/services/mastermindSpacesService";
import {spaceSharingService} from "@/services/spaceSharingService";
import {workspacesService} from "@/services/workspacesService";
import {useAuth} from "@/contexts/AuthContext";
import {useWorkspace} from "@/contexts/WorkspaceContext";
import {useOnboarding} from "@/contexts/OnboardingContext";
import {Button} from "@/components/ui/button";
import {Card, CardContent} from "@/components/ui/card";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger
} from "@/components/ui/dialog";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle
} from "@/components/ui/alert-dialog";
import {Input} from "@/components/ui/input";
import {Label} from "@/components/ui/label";
import {Textarea} from "@/components/ui/textarea";
import {Badge} from "@/components/ui/badge";
import {useToast} from "@/hooks/use-toast";
import {useViewPreferences} from "@/hooks/use-view-preferences";
import {
    Brain, Plus, Target, FileText, Sparkles, TrendingUp, Heart, DollarSign, Home, LogOut,
    Briefcase, GraduationCap, Users, LayoutGrid, List, SlidersHorizontal,
    ArrowUp, ArrowDown, ArrowUpDown, Trash2, MoreVertical, AlertTriangle, Share2, Merge, ArrowRightLeft,
    Rocket, Lightbulb, Compass, Map, Trophy, Star, Zap, Coffee, Book, Palette,
    Music, Camera, Plane, Car, Bike, Dumbbell, Apple, Leaf, Sun, Moon, Cloud,
    Globe, Flag, Shield, Lock, Key, Gift, Crown, Diamond, Gem, Coins,
    PiggyBank, Wallet, CreditCard, Building, Laptop, Code, Smartphone, Gamepad2, BookOpen,
    Award, Medal, BadgeCheck, Flame, Anchor, Timer
} from "lucide-react";
import {QuickAddTrigger} from "@/components/QuickAddInput";
import {IconSearch, IconSelectMenu} from "@/components/CompactToolbar";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuTrigger,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuItem
} from "@/components/ui/dropdown-menu";
import {Checkbox} from "@/components/ui/checkbox";
import {Table, TableBody, TableCell, TableHead, TableHeader, TableRow} from "@/components/ui/table";
import {ScrollArea} from "@/components/ui/scroll-area";
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from "@/components/ui/select";
import {formatDistanceToNow} from "date-fns";
import {EntityLockButton} from "@/components/EntityLockButton";
import {ShareSpaceDialog} from "@/components/ShareSpaceDialog";
import {ComboboxSelector} from "@/components/ComboboxSelector";

// Safe date parsing to prevent "Invalid time value" on mobile when navigating back to spaces list
function safeParseDate(dateValue: string | number | Date | null | undefined): Date | null {
    if (dateValue == null) return null;
    try {
        const date = new Date(dateValue);
        return Number.isNaN(date.getTime()) ? null : date;
    } catch {
        return null;
    }
}

function safeGetTime(dateValue: string | number | Date | null | undefined): number {
    const d = safeParseDate(dateValue);
    return d ? d.getTime() : 0;
}

function isSpaceLocked(space: MastermindSpace): boolean {
    return space.isLocked === "true";
}

/** List / grid / table use these flags from mastermindSpacesService.mapToMastermindSpace */
type SpaceListExtra = MastermindSpace & {
    isShared?: boolean;
    sharedMemberRole?: "editor" | "viewer";
    isMastermindOwner?: boolean;
    mergedIntoSpaceId?: string | null;
    deletedAt?: string | null;
    spaceStatus?: string;
};

function showSpaceOwnerActions(space: MastermindSpace, workspaceId: string | null | undefined): boolean {
    const s = space as SpaceListExtra;
    if (s.isShared) return false;
    if (workspaceId != null && workspaceId !== "") return s.isMastermindOwner === true;
    return true;
}

function sharedListRole(space: MastermindSpace): "editor" | "viewer" | null {
    const s = space as SpaceListExtra;
    if (!s.isShared) return null;
    return s.sharedMemberRole === "editor" ? "editor" : "viewer";
}

function showWorkspaceMemberSpaceHint(space: MastermindSpace, workspaceId: string | null | undefined): boolean {
    if (workspaceId == null || workspaceId === "") return false;
    const s = space as SpaceListExtra;
    if (s.isShared) return false;
    return s.isMastermindOwner !== true;
}

function spaceMergeDebugSnapshot(space: MastermindSpace | null | undefined) {
    if (!space) return null;
    const s = space as SpaceListExtra;
    return {
        id: s.id,
        name: s.name,
        workspaceId: s.workspaceId ?? null,
        isShared: s.isShared ?? false,
        isMastermindOwner: s.isMastermindOwner ?? null,
        sharedMemberRole: s.sharedMemberRole ?? null,
        spaceStatus: s.spaceStatus ?? null,
        deletedAt: s.deletedAt ?? null,
    };
}

const iconCategories = [
    {
        name: "Productivity",
        icons: [
            {name: "Brain", icon: Brain},
            {name: "Lightbulb", icon: Lightbulb},
            {name: "Target", icon: Target},
            {name: "Rocket", icon: Rocket},
            {name: "Zap", icon: Zap},
            {name: "Timer", icon: Timer},
            {name: "Flame", icon: Flame},
            {name: "Compass", icon: Compass},
        ]
    },
    {
        name: "Finance",
        icons: [
            {name: "DollarSign", icon: DollarSign},
            {name: "Wallet", icon: Wallet},
            {name: "PiggyBank", icon: PiggyBank},
            {name: "Coins", icon: Coins},
            {name: "CreditCard", icon: CreditCard},
            {name: "TrendingUp", icon: TrendingUp},
            {name: "Diamond", icon: Diamond},
            {name: "Crown", icon: Crown},
        ]
    },
    {
        name: "Career & Education",
        icons: [
            {name: "Briefcase", icon: Briefcase},
            {name: "GraduationCap", icon: GraduationCap},
            {name: "Award", icon: Award},
            {name: "Trophy", icon: Trophy},
            {name: "Medal", icon: Medal},
            {name: "BadgeCheck", icon: BadgeCheck},
            {name: "Book", icon: Book},
            {name: "BookOpen", icon: BookOpen},
        ]
    },
    {
        name: "Lifestyle",
        icons: [
            {name: "Heart", icon: Heart},
            {name: "Home", icon: Home},
            {name: "Coffee", icon: Coffee},
            {name: "Users", icon: Users},
            {name: "Gift", icon: Gift},
            {name: "Star", icon: Star},
            {name: "Sun", icon: Sun},
            {name: "Sparkles", icon: Sparkles},
        ]
    },
    {
        name: "Health & Fitness",
        icons: [
            {name: "Dumbbell", icon: Dumbbell},
            {name: "Apple", icon: Apple},
            {name: "Leaf", icon: Leaf},
            {name: "Bike", icon: Bike},
            {name: "Shield", icon: Shield},
            {name: "Anchor", icon: Anchor},
        ]
    },
    {
        name: "Tech & Creative",
        icons: [
            {name: "Laptop", icon: Laptop},
            {name: "Code", icon: Code},
            {name: "Palette", icon: Palette},
            {name: "Camera", icon: Camera},
            {name: "Music", icon: Music},
            {name: "Gamepad2", icon: Gamepad2},
            {name: "Smartphone", icon: Smartphone},
            {name: "Globe", icon: Globe},
        ]
    },
    {
        name: "Travel & Adventure",
        icons: [
            {name: "Plane", icon: Plane},
            {name: "Map", icon: Map},
            {name: "Car", icon: Car},
            {name: "Flag", icon: Flag},
            {name: "Building", icon: Building},
            {name: "Cloud", icon: Cloud},
        ]
    },
];

const colorOptions = [
    {name: "Blue", value: "hsl(220, 70%, 50%)"},
    {name: "Indigo", value: "hsl(245, 70%, 55%)"},
    {name: "Purple", value: "hsl(270, 70%, 55%)"},
    {name: "Pink", value: "hsl(330, 70%, 55%)"},
    {name: "Red", value: "hsl(0, 70%, 55%)"},
    {name: "Orange", value: "hsl(25, 85%, 55%)"},
    {name: "Amber", value: "hsl(40, 90%, 50%)"},
    {name: "Yellow", value: "hsl(50, 90%, 45%)"},
    {name: "Lime", value: "hsl(80, 70%, 45%)"},
    {name: "Green", value: "hsl(142, 70%, 45%)"},
    {name: "Emerald", value: "hsl(160, 70%, 40%)"},
    {name: "Teal", value: "hsl(175, 70%, 40%)"},
    {name: "Cyan", value: "hsl(190, 80%, 45%)"},
    {name: "Sky", value: "hsl(200, 80%, 50%)"},
    {name: "Slate", value: "hsl(215, 25%, 45%)"},
    {name: "Rose", value: "hsl(350, 70%, 55%)"},
];

const allIcons = iconCategories.flatMap(cat => cat.icons);

const iconOptions = [
    {name: "Brain", icon: Brain, color: "hsl(220, 70%, 50%)"},
    {name: "TrendingUp", icon: TrendingUp, color: "hsl(142, 71%, 45%)"},
    {name: "Heart", icon: Heart, color: "hsl(0, 84%, 60%)"},
    {name: "DollarSign", icon: DollarSign, color: "hsl(142, 76%, 36%)"},
    {name: "Home", icon: Home, color: "hsl(24, 95%, 53%)"},
    {name: "Briefcase", icon: Briefcase, color: "hsl(221, 83%, 53%)"},
    {name: "GraduationCap", icon: GraduationCap, color: "hsl(271, 76%, 53%)"},
    {name: "Users", icon: Users, color: "hsl(280, 67%, 55%)"},
    {name: "Rocket", icon: Rocket, color: "hsl(220, 70%, 50%)"},
    {name: "Lightbulb", icon: Lightbulb, color: "hsl(50, 90%, 45%)"},
    {name: "Target", icon: Target, color: "hsl(0, 70%, 55%)"},
    {name: "Zap", icon: Zap, color: "hsl(40, 90%, 50%)"},
    {name: "Trophy", icon: Trophy, color: "hsl(40, 90%, 50%)"},
    {name: "Star", icon: Star, color: "hsl(40, 90%, 50%)"},
    {name: "Compass", icon: Compass, color: "hsl(200, 80%, 50%)"},
    {name: "Map", icon: Map, color: "hsl(142, 70%, 45%)"},
    {name: "Coffee", icon: Coffee, color: "hsl(25, 85%, 55%)"},
    {name: "Book", icon: Book, color: "hsl(270, 70%, 55%)"},
    {name: "Palette", icon: Palette, color: "hsl(330, 70%, 55%)"},
    {name: "Music", icon: Music, color: "hsl(270, 70%, 55%)"},
    {name: "Camera", icon: Camera, color: "hsl(215, 25%, 45%)"},
    {name: "Plane", icon: Plane, color: "hsl(200, 80%, 50%)"},
    {name: "Car", icon: Car, color: "hsl(0, 70%, 55%)"},
    {name: "Bike", icon: Bike, color: "hsl(142, 70%, 45%)"},
    {name: "Dumbbell", icon: Dumbbell, color: "hsl(0, 70%, 55%)"},
    {name: "Apple", icon: Apple, color: "hsl(142, 70%, 45%)"},
    {name: "Leaf", icon: Leaf, color: "hsl(142, 70%, 45%)"},
    {name: "Sun", icon: Sun, color: "hsl(40, 90%, 50%)"},
    {name: "Moon", icon: Moon, color: "hsl(245, 70%, 55%)"},
    {name: "Cloud", icon: Cloud, color: "hsl(200, 80%, 50%)"},
    {name: "Globe", icon: Globe, color: "hsl(200, 80%, 50%)"},
    {name: "Flag", icon: Flag, color: "hsl(0, 70%, 55%)"},
    {name: "Shield", icon: Shield, color: "hsl(220, 70%, 50%)"},
    {name: "Lock", icon: Lock, color: "hsl(215, 25%, 45%)"},
    {name: "Key", icon: Key, color: "hsl(40, 90%, 50%)"},
    {name: "Gift", icon: Gift, color: "hsl(330, 70%, 55%)"},
    {name: "Crown", icon: Crown, color: "hsl(40, 90%, 50%)"},
    {name: "Diamond", icon: Diamond, color: "hsl(190, 80%, 45%)"},
    {name: "Gem", icon: Gem, color: "hsl(270, 70%, 55%)"},
    {name: "Coins", icon: Coins, color: "hsl(40, 90%, 50%)"},
    {name: "PiggyBank", icon: PiggyBank, color: "hsl(330, 70%, 55%)"},
    {name: "Wallet", icon: Wallet, color: "hsl(25, 85%, 55%)"},
    {name: "CreditCard", icon: CreditCard, color: "hsl(220, 70%, 50%)"},
    {name: "Building", icon: Building, color: "hsl(215, 25%, 45%)"},
    {name: "Laptop", icon: Laptop, color: "hsl(215, 25%, 45%)"},
    {name: "Code", icon: Code, color: "hsl(142, 70%, 45%)"},
    {name: "Smartphone", icon: Smartphone, color: "hsl(220, 70%, 50%)"},
    {name: "Gamepad2", icon: Gamepad2, color: "hsl(270, 70%, 55%)"},
    {name: "Sparkles", icon: Sparkles, color: "hsl(270, 70%, 55%)"},
    {name: "Award", icon: Award, color: "hsl(40, 90%, 50%)"},
    {name: "Medal", icon: Medal, color: "hsl(40, 90%, 50%)"},
    {name: "BadgeCheck", icon: BadgeCheck, color: "hsl(142, 70%, 45%)"},
    {name: "Flame", icon: Flame, color: "hsl(25, 85%, 55%)"},
    {name: "Anchor", icon: Anchor, color: "hsl(220, 70%, 50%)"},
    {name: "Timer", icon: Timer, color: "hsl(0, 70%, 55%)"},
    {name: "BookOpen", icon: BookOpen, color: "hsl(25, 85%, 55%)"},
];

interface SpaceTemplate {
    id: string;
    name: string;
    description: string;
    icon: string;
    color: string;
    goals: string[];
}

const spaceTemplates: SpaceTemplate[] = [
    {
        id: "research",
        name: "Research Project",
        description: "Organize academic or professional research with literature review, methodology, and findings.",
        icon: "BookOpen",
        color: "hsl(220, 70%, 50%)",
        goals: ["Complete literature review", "Define methodology", "Analyze findings"],
    },
    {
        id: "business",
        name: "Business Strategy",
        description: "Strategic planning with market analysis, competitor research, and action plans.",
        icon: "Briefcase",
        color: "hsl(142, 70%, 45%)",
        goals: ["Analyze market opportunity", "Identify competitive advantages", "Develop action plan"],
    },
    {
        id: "learning",
        name: "Learning Path",
        description: "Structure your learning journey from fundamentals to advanced mastery.",
        icon: "GraduationCap",
        color: "hsl(270, 70%, 55%)",
        goals: ["Master fundamentals", "Complete practice exercises", "Apply advanced techniques"],
    },
    {
        id: "content",
        name: "Content Creation",
        description: "Manage your content pipeline from ideas to publication.",
        icon: "Palette",
        color: "hsl(330, 70%, 55%)",
        goals: ["Generate 10 content ideas", "Create content calendar", "Publish consistently"],
    },
];

const getTemplateIcon = (iconName: string) => {
    switch (iconName) {
        case "BookOpen": return BookOpen;
        case "Briefcase": return Briefcase;
        case "GraduationCap": return GraduationCap;
        case "Palette": return Palette;
        default: return Brain;
    }
};

type SpaceSortField = "name" | "createdAt" | "updatedAt" | "goalCount";

interface ColumnConfig {
    id: string;
    label: string;
    visible: boolean;
}

export default function MastermindSpaces() {
    const {toast} = useToast();
    const {user} = useAuth();
    const {currentWorkspaceId, workspaces} = useWorkspace();
    const {completeMilestone, hasMilestone} = useOnboarding();
    const [location, setLocation] = useLocation();
    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
    const [spaceToShare, setSpaceToShare] = useState<MastermindSpace | null>(null);
    const [shareDialogOpen, setShareDialogOpen] = useState(false);

    const {
        viewMode,
        setViewMode,
        sortField,
        setSortField,
        sortDirection,
        setSortDirection,
    } = useViewPreferences<SpaceSortField>({
        storageKey: "mastermindSpaces",
        defaultViewMode: "grid",
        defaultSortField: "updatedAt",
        defaultSortDirection: "desc",
    });

    const [selectedSpaces, setSelectedSpaces] = useState<string[]>([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [columns, setColumns] = useState<ColumnConfig[]>(() => {
        const defaultColumns = [
            {id: "name", label: "Name", visible: true},
            {id: "description", label: "Description", visible: true},
            {id: "goals", label: "Goals", visible: true},
            {id: "sources", label: "Sources", visible: true},
            {id: "milestones", label: "Milestones", visible: true},
            {id: "updated", label: "Last Updated", visible: true},
        ];
        if (typeof window !== "undefined") {
            try {
                const saved = localStorage.getItem("mastermind-spaces-columns");
                if (saved) {
                    const parsed = JSON.parse(saved);
                    if (Array.isArray(parsed) && parsed.length > 0) {
                        return parsed;
                    }
                }
            } catch {
                // Invalid stored data, use defaults
            }
        }
        return defaultColumns;
    });
    const [newSpace, setNewSpace] = useState({
        name: "",
        description: "",
        icon: "Brain",
        color: "hsl(220, 70%, 50%)",
        goals: [] as string[],
    });
    const [goalInput, setGoalInput] = useState("");
    const [spaceToDelete, setSpaceToDelete] = useState<MastermindSpace | null>(null);
    const [deleteConfirmText, setDeleteConfirmText] = useState("");
    
    // Merge space state
    const [spaceToMerge, setSpaceToMerge] = useState<MastermindSpace | null>(null);
    const [targetMergeSpaceId, setTargetMergeSpaceId] = useState<string>("");
    const [spaceToTransfer, setSpaceToTransfer] = useState<MastermindSpace | null>(null);
    const [targetTransferWorkspaceId, setTargetTransferWorkspaceId] = useState<string>("");
    const [preserveTransferShares, setPreserveTransferShares] = useState(true);

    useEffect(() => {
        localStorage.setItem("mastermind-spaces-columns", JSON.stringify(columns));
    }, [columns]);

    const handleColumnToggle = (columnId: string) => {
        setColumns(columns.map(col =>
            col.id === columnId ? {...col, visible: !col.visible} : col
        ));
    };

    const moveColumn = (fromIndex: number, toIndex: number) => {
        const newColumns = [...columns];
        const [movedColumn] = newColumns.splice(fromIndex, 1);
        newColumns.splice(toIndex, 0, movedColumn);
        setColumns(newColumns);
    };

    const handleSort = (field: SpaceSortField) => {
        if (sortField === field) {
            setSortDirection(sortDirection === "asc" ? "desc" : "asc");
        } else {
            setSortField(field);
            setSortDirection("asc");
        }
    };

    const handleSelectAll = () => {
        if (selectedSpaces.length === filteredAndSortedSpaces.length) {
            setSelectedSpaces([]);
        } else {
            setSelectedSpaces(filteredAndSortedSpaces.map(s => s.id));
        }
    };

    const handleSelectSpace = (spaceId: string) => {
        setSelectedSpaces(prev =>
            prev.includes(spaceId) ? prev.filter(id => id !== spaceId) : [...prev, spaceId]
        );
    };

    const {data: spaces = [], isLoading, refetch: refetchSpaces} = useQuery<MastermindSpace[]>({
        queryKey: ["spaces", user?.id, currentWorkspaceId ?? "personal"],
        queryFn: async () => {
            if (!user) return [];
            try {
                return await mastermindSpacesService.getMastermindSpaces(currentWorkspaceId ?? null);
            } catch (error) {
                console.error("❌ Error fetching spaces:", error);
                throw error;
            }
        },
        enabled: !!user,
        refetchOnMount: "always",
        refetchOnWindowFocus: false,
        staleTime: 0,
    });

    const { data: pendingMergeSpaces = [] } = useQuery({
        queryKey: ["spaces", "pending-merge-archive", user?.id],
        queryFn: () => mastermindSpacesService.listPendingMergeArchiveSpaces(),
        enabled: !!user && !currentWorkspaceId,
    });

    const { data: availableWorkspaces = [] } = useQuery({
        queryKey: ["workspaces", user?.id],
        queryFn: async () => {
            if (!user) return [];
            return await workspacesService.getWorkspaces();
        },
        enabled: !!user,
    });

    // Refetch spaces when user becomes available
    useEffect(() => {
        if (user) {
            console.log("👤 User available, refetching spaces:", user.id);
            refetchSpaces();
        } else {
            console.log("⏳ Waiting for user authentication...");
        }
    }, [user?.id]); // Only depend on user.id to avoid infinite loops

    const filteredAndSortedSpaces = useMemo(() => {
        const normalizedQuery = searchQuery.trim().toLowerCase();
        const filteredSpaces = normalizedQuery
            ? spaces.filter((space) => {
                const goalsText = Array.isArray(space.goals) ? space.goals.join(" ") : "";
                return [
                    space.name,
                    space.description,
                    goalsText,
                ].some((value) => (value || "").toLowerCase().includes(normalizedQuery));
            })
            : spaces;

        // Dedupe by id so React never sees duplicate keys
        const seenIds = new Set<string>();
        const deduped = filteredSpaces.filter((s) => {
            if (seenIds.has(s.id)) return false;
            seenIds.add(s.id);
            return true;
        });

        return [...deduped].sort((a, b) => {
        let comparison = 0;

        switch (sortField) {
            case "name":
                comparison = a.name.localeCompare(b.name);
                break;
            case "createdAt":
                comparison = safeGetTime(a.createdAt) - safeGetTime(b.createdAt);
                break;
            case "updatedAt":
                comparison = safeGetTime(a.updatedAt) - safeGetTime(b.updatedAt);
                break;
            case "goalCount":
                const aGoals = Array.isArray(a.goals) ? a.goals.length : 0;
                const bGoals = Array.isArray(b.goals) ? b.goals.length : 0;
                comparison = aGoals - bGoals;
                break;
        }

            return sortDirection === "asc" ? comparison : -comparison;
        });
    }, [spaces, searchQuery, sortField, sortDirection]);

    const sharedSpaceIdsForSingularLeave = useMemo(
        () =>
            filteredAndSortedSpaces
                .filter((s) => (s as SpaceListExtra).isShared)
                .map((s) => s.id),
        [filteredAndSortedSpaces]
    );

    const sortedSingularLeaveKey = useMemo(
        () => [...sharedSpaceIdsForSingularLeave].sort().join(","),
        [sharedSpaceIdsForSingularLeave]
    );

    const {data: singularAccessBySpaceId = {}} = useQuery({
        queryKey: ["singular-space-access-batch", user?.id, sortedSingularLeaveKey],
        queryFn: () => spaceSharingService.getCurrentUserSingularAccessBatch(sharedSpaceIdsForSingularLeave),
        enabled: !!user?.id && sharedSpaceIdsForSingularLeave.length > 0,
        staleTime: 30_000,
    });

    const leaveSingularSpaceMutation = useMutation({
        mutationFn: (sid: string) => spaceSharingService.leaveSingularSpace(sid),
        onSuccess: (_data, spaceId) => {
            queryClient.invalidateQueries({queryKey: ["spaces"]});
            queryClient.invalidateQueries({queryKey: ["/api/spaces"]});
            queryClient.invalidateQueries({queryKey: ["singular-space-access-batch", user?.id]});
            queryClient.invalidateQueries({queryKey: ["/api/spaces", spaceId, "members"]});
            queryClient.invalidateQueries({queryKey: ["/api/spaces", spaceId, "my-role", user?.id]});
            toast({
                title: "Left space",
                description: "You no longer have access to this shared space.",
            });
            if (location === `/spaces/${spaceId}` || location.startsWith(`/spaces/${spaceId}/`)) {
                setLocation("/spaces");
            }
        },
        onError: (error: Error & { message?: string }) => {
            toast({
                title: "Could not leave space",
                description: error?.message || "Try again",
                variant: "destructive",
            });
        },
    });

    const SortButton = ({field, label}: { field: SpaceSortField; label: string }) => (
        <Button
            variant="ghost"
            size="sm"
            onClick={() => handleSort(field)}
            className="h-8 -ml-3"
            data-testid={`sort-${field}`}
        >
            {label}
            <ArrowUpDown className="ml-2 h-4 w-4"/>
        </Button>
    );

    const createSpaceMutation = useMutation({
        mutationFn: async (space: typeof newSpace) => {
            return await mastermindSpacesService.createMastermindSpace(
                {
                    name: space.name,
                    description: space.description,
                    icon: space.icon,
                    color: space.color,
                    goals: space.goals,
                    agents: [],
                    linkedSourceIds: [],
                    milestones: [],
                },
                currentWorkspaceId ?? undefined
            );
        },
        onSuccess: () => {
            // Invalidate all spaces queries (both old and new query keys)
            queryClient.invalidateQueries({queryKey: ["spaces"]}); // For AppSidebar
            queryClient.invalidateQueries({queryKey: ["/api/spaces"]}); // For this page
            refetchSpaces(); // Also manually refetch
            
            // Trigger first_space milestone for new users (unlocks Library)
            if (!hasMilestone('first_space')) {
                completeMilestone('first_space');
            }
            
            toast({
                title: "Space Created",
                description: "Your new Mastermind Space has been created successfully",
            });
            setIsCreateDialogOpen(false);
            resetForm();
        },
        onError: () => {
            toast({
                title: "Failed to Create Space",
                description: "There was an error creating your Mastermind Space",
                variant: "destructive",
            });
        },
    });

    const deleteSpaceMutation = useMutation({
        mutationFn: async (spaceId: string) => {
            return await mastermindSpacesService.deleteMastermindSpace(spaceId, currentWorkspaceId ?? null);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ["/api/spaces"]}); // Invalidate all spaces queries
            refetchSpaces(); // Also manually refetch
            toast({
                title: "Space Deleted",
                description: "The Mastermind Space and all its contents have been permanently deleted",
            });
            setSpaceToDelete(null);
            setDeleteConfirmText("");
        },
        onError: () => {
            toast({
                title: "Failed to Delete Space",
                description: "There was an error deleting the Mastermind Space",
                variant: "destructive",
            });
        },
    });

    const handleDeleteSpace = (space: MastermindSpace, e: React.MouseEvent) => {
        e.stopPropagation();
        setSpaceToDelete(space);
        setDeleteConfirmText("");
    };

    const confirmDelete = () => {
        if (spaceToDelete && deleteConfirmText === spaceToDelete.name) {
            deleteSpaceMutation.mutate(spaceToDelete.id);
        }
    };

    // Merge space mutation
    const mergeSpaceMutation = useMutation({
        mutationFn: async ({ sourceId, targetId }: { sourceId: string; targetId: string }) => {
            const targetSpace = (spaces || []).find((s) => s.id === targetId);
            const currentWorkspaceRole = currentWorkspaceId
                ? workspaces.find((w) => w.id === currentWorkspaceId)?.role ?? null
                : null;

            console.group("[SpaceMergeDebug] MastermindSpaces merge mutation");
            console.log("userId", user?.id ?? null);
            console.log("currentWorkspaceId", currentWorkspaceId ?? null);
            console.log("currentWorkspaceRole", currentWorkspaceRole);
            console.log("sourceSpace", spaceMergeDebugSnapshot(spaceToMerge));
            console.log("targetSpace", spaceMergeDebugSnapshot(targetSpace));
            console.log("showSpaceOwnerActions", spaceToMerge ? showSpaceOwnerActions(spaceToMerge, currentWorkspaceId) : null);
            console.log("mergeDestinationOptionsCount", mergeDestinationOptions.length);
            console.log("request", { sourceId, targetId });
            console.groupEnd();

            return await mastermindSpacesService.mergeSpaceInto(sourceId, targetId);
        },
        onSuccess: (result) => {
            // Invalidate all spaces queries
            queryClient.invalidateQueries({ queryKey: ["spaces"] }); // For AppSidebar
            queryClient.invalidateQueries({ queryKey: ["/api/spaces"] }); // For this page
            queryClient.invalidateQueries({ queryKey: ["/api/knowledge-base"] });
            queryClient.invalidateQueries({ queryKey: ["knowledgebase-summary"] });
            queryClient.invalidateQueries({ queryKey: ["/api/sources"] });
            queryClient.invalidateQueries({ queryKey: ["spaces", "pending-merge-archive"] });
            refetchSpaces();
            toast({
                title: "Spaces Merged",
                description: result.message,
            });
            setSpaceToMerge(null);
            setTargetMergeSpaceId("");
        },
        onError: (error: Error) => {
            const targetSpace = (spaces || []).find((s) => s.id === targetMergeSpaceId);
            const currentWorkspaceRole = currentWorkspaceId
                ? workspaces.find((w) => w.id === currentWorkspaceId)?.role ?? null
                : null;

            console.warn("[SpaceMergeDebug] MastermindSpaces merge failed", {
                userId: user?.id ?? null,
                currentWorkspaceId: currentWorkspaceId ?? null,
                currentWorkspaceRole,
                sourceSpace: spaceMergeDebugSnapshot(spaceToMerge),
                targetSpace: spaceMergeDebugSnapshot(targetSpace),
                showSpaceOwnerActions: spaceToMerge ? showSpaceOwnerActions(spaceToMerge, currentWorkspaceId) : null,
                mergeDestinationOptionsCount: mergeDestinationOptions.length,
                errorMessage: error.message,
                error,
            });

            toast({
                title: "Merge Failed",
                description: error.message,
                variant: "destructive",
            });
        },
    });

    const handleMergeSpace = (space: MastermindSpace, e: React.MouseEvent) => {
        e.stopPropagation();
        setSpaceToMerge(space);
        setTargetMergeSpaceId("");
    };

    const confirmMerge = () => {
        if (spaceToMerge && targetMergeSpaceId) {
            mergeSpaceMutation.mutate({ sourceId: spaceToMerge.id, targetId: targetMergeSpaceId });
        }
    };

    const restoreMergeMutation = useMutation({
        mutationFn: (sourceSpaceId: string) => mastermindSpacesService.restoreMergedSpace(sourceSpaceId),
        onSuccess: (res) => {
            queryClient.invalidateQueries({ queryKey: ["spaces"] });
            queryClient.invalidateQueries({ queryKey: ["/api/spaces"], exact: false });
            queryClient.invalidateQueries({ queryKey: ["knowledgebase"], exact: false });
            queryClient.invalidateQueries({ queryKey: ["/api/knowledgebase"], exact: false });
            queryClient.invalidateQueries({ queryKey: ["knowledgebase-summary"], exact: false });
            queryClient.invalidateQueries({ queryKey: ["/api/chat-thread-summaries"], exact: false });
            queryClient.invalidateQueries({ queryKey: ["/api/topics"], exact: false });
            queryClient.invalidateQueries({ queryKey: ["/api/sources"], exact: false });
            queryClient.invalidateQueries({ queryKey: ["spaces", "pending-merge-archive"] });
            if (res.destinationSpaceId) {
                queryClient.invalidateQueries({
                    queryKey: ["/api/spaces", res.destinationSpaceId],
                    exact: false,
                });
            }
            queryClient.invalidateQueries({
                queryKey: ["/api/spaces", res.restoredSpaceId],
                exact: false,
            });
            refetchSpaces();
            toast({ title: "Merge undone", description: res.message });
        },
        onError: (error: Error) => {
            toast({ title: "Restore failed", description: error.message, variant: "destructive" });
        },
    });

    /** Select value for moving a space back to personal (no workspace). */
    const TRANSFER_TARGET_PERSONAL = "__personal__";

    const transferSpaceMutation = useMutation({
        mutationFn: async ({
            spaceId,
            targetWorkspaceId,
            preserveShares,
        }: {
            spaceId: string;
            targetWorkspaceId: string | null;
            preserveShares: boolean;
        }) => {
            return await mastermindSpacesService.transferSpaceToWorkspace(spaceId, targetWorkspaceId, { preserveShares });
        },
        onSuccess: (result) => {
            queryClient.invalidateQueries({ queryKey: ["spaces"] });
            queryClient.invalidateQueries({ queryKey: ["/api/spaces"] });
            queryClient.invalidateQueries({ queryKey: ["threads"] });
            queryClient.invalidateQueries({ queryKey: ["chat-threads"] });
            queryClient.invalidateQueries({ queryKey: ["knowledgebase"] });
            queryClient.invalidateQueries({ queryKey: ["sources"] });
            queryClient.invalidateQueries({ queryKey: ["topics"] });
            refetchSpaces();
            toast({
                title: "Space Transferred",
                description: `${result.message} ${result.topicsMoved} topics, ${result.threadsMoved} threads moved.`,
            });
            setSpaceToTransfer(null);
            setTargetTransferWorkspaceId("");
            setPreserveTransferShares(true);
        },
        onError: (error: Error) => {
            toast({
                title: "Transfer Failed",
                description: error.message,
                variant: "destructive",
            });
        },
    });

    const handleTransferSpace = (space: MastermindSpace, e: React.MouseEvent) => {
        e.stopPropagation();
        setSpaceToTransfer(space);
        setTargetTransferWorkspaceId("");
        setPreserveTransferShares(true);
    };

    const confirmTransfer = () => {
        if (!spaceToTransfer || !targetTransferWorkspaceId) return;
        const resolvedTarget =
            targetTransferWorkspaceId === TRANSFER_TARGET_PERSONAL ? null : targetTransferWorkspaceId;
        transferSpaceMutation.mutate({
            spaceId: spaceToTransfer.id,
            targetWorkspaceId: resolvedTarget,
            preserveShares: preserveTransferShares,
        });
    };

    const resetForm = () => {
        setNewSpace({
            name: "",
            description: "",
            icon: "Brain",
            color: "hsl(220, 70%, 50%)",
            goals: [],
        });
        setGoalInput("");
    };

    const handleAddGoal = () => {
        if (goalInput.trim()) {
            setNewSpace({...newSpace, goals: [...newSpace.goals, goalInput.trim()]});
            setGoalInput("");
        }
    };

    const handleRemoveGoal = (index: number) => {
        setNewSpace({...newSpace, goals: newSpace.goals.filter((_, i) => i !== index)});
    };

    const handleCreateSpace = () => {
        if (!newSpace.name.trim()) {
            toast({
                title: "Name Required",
                description: "Please enter a name for your Mastermind Space",
                variant: "destructive",
            });
            return;
        }
        createSpaceMutation.mutate(newSpace);
    };

    const getIconComponent = (iconName: string) => {
        const option = iconOptions.find(opt => opt.name === iconName);
        return option ? option.icon : Brain;
    };

    const mergeDestinationOptions = useMemo(() => {
        if (!spaceToMerge) return [];
        return (spaces || [])
            .filter((s) => s.id !== spaceToMerge.id && !(s as { isShared?: boolean }).isShared)
            .map((s) => {
                const IconComponent = getIconComponent(s.icon || "Brain");
                return {
                    value: s.id,
                    label: s.name,
                    icon: (
                        <IconComponent
                            className="h-4 w-4 shrink-0"
                            style={{ color: s.color || undefined }}
                        />
                    ),
                };
            });
    }, [spaces, spaceToMerge]);

    const renderSpaceOverflowMenu = (space: MastermindSpace, variant: "table" | "grid" | "list") => {
        const shareTid =
            variant === "table"
                ? `button-table-share-space-${space.id}`
                : variant === "list"
                  ? `button-list-share-space-${space.id}`
                  : `button-share-space-${space.id}`;
        const mergeTid =
            variant === "table"
                ? `button-table-merge-space-${space.id}`
                : variant === "list"
                  ? `button-list-merge-space-${space.id}`
                  : `button-merge-space-${space.id}`;
        const transferTid =
            variant === "table"
                ? `button-table-transfer-space-${space.id}`
                : variant === "list"
                  ? `button-list-transfer-space-${space.id}`
                  : `button-transfer-space-${space.id}`;
        const deleteTid =
            variant === "table"
                ? `button-table-delete-space-${space.id}`
                : variant === "list"
                  ? `button-list-delete-space-${space.id}`
                  : `button-delete-space-${space.id}`;

        const owner = showSpaceOwnerActions(space, currentWorkspaceId);
        const sr = sharedListRole(space);
        const wsHint = showWorkspaceMemberSpaceHint(space, currentWorkspaceId);
        const leaveInfo = singularAccessBySpaceId[space.id];
        const canLeaveSingular = leaveInfo?.canLeave === true;

        return (
            <>
                {owner && (
                    <>
                        <DropdownMenuItem
                            onClick={(e) => {
                                e.stopPropagation();
                                setSpaceToShare(space);
                                setShareDialogOpen(true);
                            }}
                            data-testid={shareTid}
                        >
                            <Share2 className="h-4 w-4 mr-2"/>
                            Share Space
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={(e) => handleMergeSpace(space, e)} data-testid={mergeTid}>
                            <Merge className="h-4 w-4 mr-2"/>
                            Merge into Space
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={(e) => handleTransferSpace(space, e)} data-testid={transferTid}>
                            <ArrowRightLeft className="h-4 w-4 mr-2"/>
                            Transfer to Workspace
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                            className="text-destructive focus:text-destructive"
                            onClick={(e) => handleDeleteSpace(space, e)}
                            data-testid={deleteTid}
                        >
                            <Trash2 className="h-4 w-4 mr-2"/>
                            Delete Space
                        </DropdownMenuItem>
                    </>
                )}
                {!owner && (sr === "viewer" || sr === "editor") && canLeaveSingular && (
                    <DropdownMenuItem
                        className="text-destructive focus:text-destructive"
                        onClick={(e) => {
                            e.stopPropagation();
                            if (confirm("Leave this shared space? You will need a new invite to return.")) {
                                leaveSingularSpaceMutation.mutate(space.id);
                            }
                        }}
                        disabled={leaveSingularSpaceMutation.isPending}
                    >
                        <LogOut className="h-4 w-4 mr-2"/>
                        {leaveSingularSpaceMutation.isPending ? "Leaving…" : "Leave space"}
                    </DropdownMenuItem>
                )}
                {!owner && sr === "viewer" && !canLeaveSingular && (
                    <DropdownMenuItem disabled>
                        <span className="text-xs text-muted-foreground">
                            {leaveInfo?.mirroredFromWorkspace
                                ? "Workspace share — view only (manage access in Workspace settings)"
                                : "Shared with you — view only"}
                        </span>
                    </DropdownMenuItem>
                )}
                {!owner && sr === "editor" && !canLeaveSingular && (
                    <DropdownMenuItem disabled>
                        <span className="text-xs text-muted-foreground">
                            {leaveInfo?.mirroredFromWorkspace
                                ? "Workspace share — open the space to edit (manage access in Workspace settings)"
                                : "Shared with you (editor) — open the space to edit content"}
                        </span>
                    </DropdownMenuItem>
                )}
                {!owner && sr === null && wsHint && (
                    <DropdownMenuItem disabled>
                        <span className="text-xs text-muted-foreground">
                            Only the space creator can share, merge, or delete this space
                        </span>
                    </DropdownMenuItem>
                )}
            </>
        );
    };

    return (
        <div className="h-full flex flex-col">
            {/* Compact Header */}
            <div
                className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-0 z-10">
                {/* DS EDIT 5aug26 — single header row: title + search/sort/view icons + New Space */}
                <div className="flex items-center justify-between gap-2 px-3 py-2 pr-20">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                        <Brain className="h-4 w-4 text-primary shrink-0"/>
                        <h1 className="text-base font-semibold truncate" data-testid="heading-mastermind-spaces">Spaces</h1>
                        <Badge variant="secondary" className="text-xs shrink-0">{spaces.length}</Badge>
                        <IconSearch
                            value={searchQuery}
                            onChange={setSearchQuery}
                            placeholder="Search spaces..."
                            testId="input-spaces-search"
                        />
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                    <QuickAddTrigger/>
                    <IconSelectMenu
                        label="Sort spaces"
                        value={`${sortField}-${sortDirection}`}
                        onValueChange={(value) => {
                            const [field, direction] = value.split("-") as [SpaceSortField, "asc" | "desc"];
                            setSortField(field);
                            setSortDirection(direction);
                        }}
                        testId="select-sort"
                        options={[
                            {value: "name-asc", label: "A → Z", icon: ArrowUp, testId: "sort-name-asc"},
                            {value: "name-desc", label: "Z → A", icon: ArrowDown, testId: "sort-name-desc"},
                            {value: "createdAt-desc", label: "Newest first", icon: ArrowDown, testId: "sort-created-desc"},
                            {value: "createdAt-asc", label: "Oldest first", icon: ArrowUp, testId: "sort-created-asc"},
                            {value: "updatedAt-desc", label: "Recent", icon: ArrowDown, testId: "sort-updated-desc"},
                            {value: "updatedAt-asc", label: "Least recent", icon: ArrowUp, testId: "sort-updated-asc"},
                        ]}
                    />
                    <div className="flex items-center gap-0.5 border rounded-md p-0.5">
                        <Button
                            variant={viewMode === "grid" ? "secondary" : "ghost"}
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => setViewMode("grid")}
                            data-testid="button-view-grid"
                            title="Grid view"
                        >
                            <LayoutGrid className="h-3.5 w-3.5"/>
                        </Button>
                        <Button
                            variant={viewMode === "list" ? "secondary" : "ghost"}
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => setViewMode("list")}
                            data-testid="button-view-list"
                            title="List view"
                        >
                            <List className="h-3.5 w-3.5"/>
                        </Button>
                        <Button
                            variant={viewMode === "table" ? "secondary" : "ghost"}
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => setViewMode("table")}
                            data-testid="button-view-table"
                            title="Table view"
                        >
                            <FileText className="h-3.5 w-3.5"/>
                        </Button>
                    </div>
                    {viewMode === "table" && (
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm" className="h-8" data-testid="button-column-controls">
                                    <SlidersHorizontal className="h-3.5 w-3.5"/>
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-[220px]">
                                <DropdownMenuLabel className="text-xs">Columns</DropdownMenuLabel>
                                <DropdownMenuSeparator/>
                                {columns.map((column, index) => (
                                    <div key={column.id}
                                         className="flex items-center gap-2 px-2 py-1 hover-elevate rounded">
                                        <Checkbox
                                            checked={column.visible}
                                            onCheckedChange={() => handleColumnToggle(column.id)}
                                            data-testid={`toggle-column-${column.id}`}
                                        />
                                        <span className="flex-1 text-xs">{column.label}</span>
                                        <div className="flex gap-0.5">
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-5 w-5"
                                                onClick={() => moveColumn(index, Math.max(0, index - 1))}
                                                disabled={index === 0}
                                                data-testid={`move-column-up-${column.id}`}
                                            >
                                                <ArrowUp className="h-2.5 w-2.5"/>
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-5 w-5"
                                                onClick={() => moveColumn(index, Math.min(columns.length - 1, index + 1))}
                                                disabled={index === columns.length - 1}
                                                data-testid={`move-column-down-${column.id}`}
                                            >
                                                <ArrowDown className="h-2.5 w-2.5"/>
                                            </Button>
                                        </div>
                                    </div>
                                ))}
                            </DropdownMenuContent>
                        </DropdownMenu>
                    )}
                    <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
                        <DialogTrigger asChild>
                            <Button size="sm" data-testid="button-create-space">
                                <Plus className="h-4 w-4 mr-1"/>
                                <span className="hidden sm:inline">New Space</span>
                            </Button>
                        </DialogTrigger>
                            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                                <DialogHeader>
                                    <DialogTitle>Create Mastermind Space</DialogTitle>
                                    <DialogDescription>
                                        Build a specialized AI system for managing a major life domain
                                    </DialogDescription>
                                </DialogHeader>

                                <div className="space-y-6 py-4">
                                    {/* Quick Start Templates */}
                                    <div className="space-y-2">
                                        <Label className="flex items-center gap-2">
                                            <Sparkles className="h-4 w-4 text-primary"/>
                                            Quick Start from Template
                                        </Label>
                                        <div className="grid grid-cols-2 gap-2">
                                            {spaceTemplates.map((template) => {
                                                const TemplateIcon = getTemplateIcon(template.icon);
                                                return (
                                                    <button
                                                        key={template.id}
                                                        onClick={() => setNewSpace({
                                                            name: template.name,
                                                            description: template.description,
                                                            icon: template.icon,
                                                            color: template.color,
                                                            goals: template.goals,
                                                        })}
                                                        className="flex items-start gap-2 p-3 rounded-lg border hover:border-primary hover:bg-accent/50 transition-colors text-left"
                                                        data-testid={`template-${template.id}`}
                                                    >
                                                        <div 
                                                            className="p-2 rounded-md shrink-0"
                                                            style={{ backgroundColor: `${template.color}20` }}
                                                        >
                                                            <TemplateIcon className="h-4 w-4" style={{ color: template.color }} />
                                                        </div>
                                                        <div className="min-w-0">
                                                            <p className="font-medium text-sm truncate">{template.name}</p>
                                                            <p className="text-xs text-muted-foreground line-clamp-2">{template.description}</p>
                                                        </div>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    <div className="relative">
                                        <div className="absolute inset-0 flex items-center">
                                            <span className="w-full border-t" />
                                        </div>
                                        <div className="relative flex justify-center text-xs uppercase">
                                            <span className="bg-background px-2 text-muted-foreground">or customize</span>
                                        </div>
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="space-name">Space Name</Label>
                                        <Input
                                            id="space-name"
                                            data-testid="input-space-name"
                                            placeholder="e.g., Wealth Building, Career Growth, Health & Wellness"
                                            value={newSpace.name}
                                            onChange={(e) => setNewSpace({...newSpace, name: e.target.value})}
                                        />
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="space-description">Description</Label>
                                        <Textarea
                                            id="space-description"
                                            data-testid="textarea-space-description"
                                            placeholder="What is this Mastermind Space for? What goals are you trying to achieve?"
                                            value={newSpace.description}
                                            onChange={(e) => setNewSpace({...newSpace, description: e.target.value})}
                                            rows={3}
                                        />
                                    </div>

                                    <div className="space-y-3">
                                        <div className="flex items-center gap-3">
                                            <Label>Preview</Label>
                                            <div
                                                className="p-2.5 rounded-lg"
                                                style={{backgroundColor: `${newSpace.color}20`}}
                                            >
                                                {(() => {
                                                    const IconComp = allIcons.find(i => i.name === newSpace.icon)?.icon || Brain;
                                                    return <IconComp className="h-6 w-6"
                                                                     style={{color: newSpace.color}}/>;
                                                })()}
                                            </div>
                                        </div>

                                        <div className="space-y-2">
                                            <Label className="text-xs text-muted-foreground">Pick a Color</Label>
                                            <div className="flex flex-wrap gap-1.5">
                                                {colorOptions.map((color) => (
                                                    <button
                                                        key={color.name}
                                                        data-testid={`button-color-${color.name.toLowerCase()}`}
                                                        onClick={() => setNewSpace({...newSpace, color: color.value})}
                                                        className={`w-7 h-7 rounded-full border-2 hover:scale-110 transition-transform ${
                                                            newSpace.color === color.value ? "border-foreground ring-2 ring-offset-2 ring-offset-background" : "border-transparent"
                                                        }`}
                                                        style={{backgroundColor: color.value}}
                                                        title={color.name}
                                                    />
                                                ))}
                                            </div>
                                        </div>

                                        <div className="space-y-2">
                                            <Label className="text-xs text-muted-foreground">Pick an Icon</Label>
                                            <ScrollArea className="h-[180px] rounded-md border p-2">
                                                <div className="space-y-3">
                                                    {iconCategories.map((category) => (
                                                        <div key={category.name}>
                                                            <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider mb-1.5">{category.name}</p>
                                                            <div className="flex flex-wrap gap-1">
                                                                {category.icons.map((iconItem) => {
                                                                    const IconComponent = iconItem.icon;
                                                                    const isSelected = newSpace.icon === iconItem.name;
                                                                    return (
                                                                        <button
                                                                            key={iconItem.name}
                                                                            data-testid={`button-icon-${iconItem.name.toLowerCase()}`}
                                                                            onClick={() => setNewSpace({
                                                                                ...newSpace,
                                                                                icon: iconItem.name
                                                                            })}
                                                                            className={`p-1.5 rounded-md border hover:bg-accent transition-colors ${
                                                                                isSelected ? "border-primary bg-accent" : "border-transparent"
                                                                            }`}
                                                                            title={iconItem.name}
                                                                        >
                                                                            <IconComponent
                                                                                className="h-4 w-4"
                                                                                style={{color: isSelected ? newSpace.color : undefined}}
                                                                            />
                                                                        </button>
                                                                    );
                                                                })}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </ScrollArea>
                                        </div>
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="goal-input">Long-term Goals</Label>
                                        <div className="flex gap-2">
                                            <Input
                                                id="goal-input"
                                                data-testid="input-goal"
                                                placeholder="Add a goal (e.g., Achieve financial independence by 2030)"
                                                value={goalInput}
                                                onChange={(e) => setGoalInput(e.target.value)}
                                                onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), handleAddGoal())}
                                            />
                                            <Button
                                                type="button"
                                                onClick={handleAddGoal}
                                                data-testid="button-add-goal"
                                                variant="outline"
                                            >
                                                <Plus className="h-4 w-4"/>
                                            </Button>
                                        </div>
                                        {newSpace.goals.length > 0 && (
                                            <div className="space-y-2 mt-3">
                                                {newSpace.goals.map((goal, index) => (
                                                    <div
                                                        key={index}
                                                        className="flex items-center justify-between p-2 rounded-md bg-accent"
                                                        data-testid={`goal-item-${index}`}
                                                    >
                                                        <div className="flex items-center gap-2">
                                                            <Target className="h-4 w-4 text-muted-foreground"/>
                                                            <span className="text-sm">{goal}</span>
                                                        </div>
                                                        <Button
                                                            type="button"
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => handleRemoveGoal(index)}
                                                            data-testid={`button-remove-goal-${index}`}
                                                        >
                                                            ×
                                                        </Button>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <DialogFooter>
                                    <Button
                                        variant="outline"
                                        onClick={() => setIsCreateDialogOpen(false)}
                                        data-testid="button-cancel-create"
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        onClick={handleCreateSpace}
                                        disabled={createSpaceMutation.isPending}
                                        data-testid="button-submit-create"
                                    >
                                        {createSpaceMutation.isPending ? "Creating..." : "Create Space"}
                                    </Button>
                                </DialogFooter>
                            </DialogContent>
                        </Dialog>
                    </div>
                </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
                {!currentWorkspaceId && pendingMergeSpaces.length > 0 && (
                    <Card className="mb-4 border-amber-500/35 bg-amber-500/[0.06]">
                        <CardContent className="py-3 px-4">
                            <div className="flex items-start gap-2 mb-2">
                                <Timer className="h-4 w-4 text-amber-600 shrink-0 mt-0.5"/>
                                <div>
                                    <p className="text-sm font-medium">Merged spaces — restorable for 72 hours</p>
                                    <p className="text-xs text-muted-foreground mt-0.5">
                                        After that, empty archived shells are removed automatically. Restore to undo the merge.
                                    </p>
                                </div>
                            </div>
                            <ul className="space-y-2 mt-3">
                                {pendingMergeSpaces.map((sp) => {
                                    const s = sp as SpaceListExtra;
                                    const del = safeParseDate(s.deletedAt ?? null);
                                    const purgeHint = del
                                        ? formatDistanceToNow(new Date(del.getTime() + 72 * 60 * 60 * 1000), {
                                              addSuffix: true,
                                          })
                                        : null;
                                    return (
                                        <li
                                            key={s.id}
                                            className="flex flex-wrap items-center justify-between gap-2 text-sm border rounded-md px-3 py-2 bg-background/80"
                                        >
                                            <div>
                                                <span className="font-medium">{s.name}</span>
                                                {s.mergedIntoSpaceId && (
                                                    <span className="text-muted-foreground">
                                                        {" "}
                                                        (merged into another space)
                                                    </span>
                                                )}
                                                {purgeHint && (
                                                    <span className="block text-xs text-muted-foreground mt-0.5">
                                                        Permanent removal {purgeHint}
                                                    </span>
                                                )}
                                            </div>
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                disabled={restoreMergeMutation.isPending}
                                                onClick={() => restoreMergeMutation.mutate(s.id)}
                                                data-testid={`button-restore-merge-${s.id}`}
                                            >
                                                Restore
                                            </Button>
                                        </li>
                                    );
                                })}
                            </ul>
                        </CardContent>
                    </Card>
                )}
                {isLoading ? (
                    <div className="text-center text-muted-foreground py-8">Loading spaces...</div>
                ) : filteredAndSortedSpaces.length === 0 ? (
                    <div className="text-center py-8">
                        <Sparkles className="h-10 w-10 mx-auto text-muted-foreground mb-3"/>
                        <h3 className="text-base font-medium mb-1">No Mastermind Spaces Yet</h3>
                        <p className="text-sm text-muted-foreground mb-4 max-w-sm mx-auto">
                            Create your first space to organize your goals and knowledge
                        </p>
                        <Button size="sm" onClick={() => setIsCreateDialogOpen(true)}
                                data-testid="button-create-first-space">
                            <Plus className="h-4 w-4 mr-1"/>
                            Create Your First Space
                        </Button>
                    </div>
                ) : viewMode === "table" ? (
                    <Card>
                        <ScrollArea className="w-full">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead className="w-12">
                                            <Checkbox
                                                checked={selectedSpaces.length === filteredAndSortedSpaces.length && filteredAndSortedSpaces.length > 0}
                                                onCheckedChange={handleSelectAll}
                                                data-testid="checkbox-select-all"
                                            />
                                        </TableHead>
                                        {columns.filter(c => c.visible).map(column => {
                                            switch (column.id) {
                                                case "name":
                                                    return (<TableHead key="name"><SortButton field="name"
                                                                                              label="Name"/></TableHead>);
                                                case "description":
                                                    return <TableHead key="description">Description</TableHead>;
                                                case "goals":
                                                    return (<TableHead key="goals"><SortButton field="goalCount"
                                                                                               label="Goals"/></TableHead>);
                                                case "sources":
                                                    return <TableHead key="sources">Sources</TableHead>;
                                                case "milestones":
                                                    return <TableHead key="milestones">Milestones</TableHead>;
                                                case "updated":
                                                    return (<TableHead key="updated"><SortButton field="updatedAt"
                                                                                                 label="Last Updated"/></TableHead>);
                                                default:
                                                    return null;
                                            }
                                        })}
                                        <TableHead className="w-12"></TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {filteredAndSortedSpaces.map((space) => {
                                        const IconComponent = getIconComponent(space.icon || "Brain");
                                        const goalCount = Array.isArray(space.goals) ? space.goals.length : 0;
                                        const sourceCount = Array.isArray(space.linkedSourceIds) ? space.linkedSourceIds.length : 0;
                                        const milestones = Array.isArray(space.milestones) ? space.milestones : [];

                                        return (
                                            <TableRow
                                                key={space.id}
                                                className="hover-elevate cursor-pointer"
                                                onClick={() => setLocation(`/spaces/${space.id}`)}
                                                data-testid={`space-row-${space.id}`}
                                            >
                                                <TableCell onClick={(e) => e.stopPropagation()}>
                                                    <Checkbox
                                                        checked={selectedSpaces.includes(space.id)}
                                                        onCheckedChange={() => handleSelectSpace(space.id)}
                                                        data-testid={`checkbox-space-${space.id}`}
                                                    />
                                                </TableCell>
                                                {columns.filter(c => c.visible).map(column => {
                                                    switch (column.id) {
                                                        case "name":
                                                            return (
                                                                <TableCell key="name" className="font-medium">
                                                                    <div className="flex items-center gap-2">
                                                                        {isSpaceLocked(space) ? (
                                                                            <Lock
                                                                                className="h-4 w-4 text-amber-500 shrink-0"/>
                                                                        ) : (
                                                                            <div
                                                                                className="p-1.5 rounded"
                                                                                style={{backgroundColor: `${space.color || "hsl(220, 70%, 50%)"}15`}}
                                                                            >
                                                                                <IconComponent
                                                                                    className="h-4 w-4"
                                                                                    style={{color: space.color || "hsl(220, 70%, 50%)"}}
                                                                                />
                                                                            </div>
                                                                        )}
                                                                        <span
                                                                            className="max-w-md truncate">{space.name}</span>
                                                                        {(space as SpaceListExtra).isShared && (
                                                                            <Badge variant="secondary"
                                                                                   className="text-[10px] px-1.5 py-0 h-4 shrink-0">
                                                                                Shared
                                                                            </Badge>
                                                                        )}
                                                                        {(space as SpaceListExtra).isShared &&
                                                                            (space as SpaceListExtra).sharedMemberRole === "editor" && (
                                                                            <Badge variant="outline"
                                                                                   className="text-[10px] px-1.5 py-0 h-4 shrink-0 border-primary/40 text-primary">
                                                                                Can edit
                                                                            </Badge>
                                                                        )}
                                                                        {isSpaceLocked(space) && (
                                                                            <Badge variant="outline"
                                                                                   className="text-[10px] px-1.5 py-0 h-4 shrink-0 text-amber-500 border-amber-500/30">
                                                                                Locked
                                                                            </Badge>
                                                                        )}
                                                                    </div>
                                                                </TableCell>
                                                            );
                                                        case "description":
                                                            return (
                                                                <TableCell key="description">
                                  <span className="text-sm text-muted-foreground line-clamp-1">
                                    {space.description || "—"}
                                  </span>
                                                                </TableCell>
                                                            );
                                                        case "goals":
                                                            return (
                                                                <TableCell key="goals">
                                                                    <span className="text-sm">{goalCount}</span>
                                                                </TableCell>
                                                            );
                                                        case "sources":
                                                            return (
                                                                <TableCell key="sources">
                                                                    <span className="text-sm">{sourceCount}</span>
                                                                </TableCell>
                                                            );
                                                        case "milestones":
                                                            return (
                                                                <TableCell key="milestones">
                                                                    <span className="text-sm">{milestones.length}</span>
                                                                </TableCell>
                                                            );
                                                        case "updated": {
                                                            const updatedDate = safeParseDate(space.updatedAt);
                                                            return (
                                                                <TableCell key="updated">
                                  <span className="text-sm text-muted-foreground">
                                    {updatedDate ? formatDistanceToNow(updatedDate, {addSuffix: true}) : "—"}
                                  </span>
                                                                </TableCell>
                                                            );
                                                            }
                                                        default:
                                                            return null;
                                                    }
                                                })}
                                                <TableCell onClick={(e) => e.stopPropagation()}>
                                                    <div className="flex items-center gap-1">
                                                        <EntityLockButton
                                                            entityType="space"
                                                            entityId={space.id}
                                                            isLocked={isSpaceLocked(space)}
                                                            workspaceId={currentWorkspaceId ?? undefined}
                                                        />
                                                        <DropdownMenu>
                                                            <DropdownMenuTrigger asChild>
                                                                <Button
                                                                    variant="ghost"
                                                                    size="icon"
                                                                    className="h-7 w-7"
                                                                    data-testid={`button-table-space-menu-${space.id}`}
                                                                >
                                                                    <MoreVertical className="h-4 w-4"/>
                                                                </Button>
                                                            </DropdownMenuTrigger>
                                                            <DropdownMenuContent align="end">
                                                                {renderSpaceOverflowMenu(space, "table")}
                                                            </DropdownMenuContent>
                                                        </DropdownMenu>
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </ScrollArea>
                    </Card>
                ) : viewMode === "grid" ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                        {filteredAndSortedSpaces.map((space) => {
                            const IconComponent = getIconComponent(space.icon || "Brain");

                            return (
                                <Card
                                    key={space.id}
                                    className="hover-elevate cursor-pointer group"
                                    data-testid={`card-space-${space.id}`}
                                    onClick={() => setLocation(`/spaces/${space.id}`)}
                                >
                                    <CardContent className="p-3">
                                        <div className="flex items-start gap-2.5 mb-2">
                                            {isSpaceLocked(space) ? (
                                                <div className="p-1.5 rounded-md flex-shrink-0 bg-amber-500/10">
                                                    <Lock className="h-4 w-4 text-amber-500"/>
                                                </div>
                                            ) : (
                                                <div
                                                    className="p-1.5 rounded-md flex-shrink-0"
                                                    style={{backgroundColor: `${space.color || "hsl(220, 70%, 50%)"}15`}}
                                                >
                                                    <IconComponent
                                                        className="h-4 w-4"
                                                        style={{color: space.color || "hsl(220, 70%, 50%)"}}
                                                    />
                                                </div>
                                            )}
                                            <div className="min-w-0 flex-1">
                                                <div className="flex items-center gap-1.5">
                                                    <h3 className="font-medium text-sm truncate">{space.name}</h3>
                                                    {(space as SpaceListExtra).isShared && (
                                                        <Badge variant="secondary"
                                                               className="text-[10px] px-1.5 py-0 h-4 shrink-0">
                                                            Shared
                                                        </Badge>
                                                    )}
                                                    {(space as SpaceListExtra).isShared &&
                                                        (space as SpaceListExtra).sharedMemberRole === "editor" && (
                                                        <Badge variant="outline"
                                                               className="text-[10px] px-1.5 py-0 h-4 shrink-0 border-primary/40 text-primary">
                                                            Can edit
                                                        </Badge>
                                                    )}
                                                    {isSpaceLocked(space) && (
                                                        <Badge variant="outline"
                                                               className="text-[10px] px-1.5 py-0 h-4 shrink-0 text-amber-500 border-amber-500/30">
                                                            Locked
                                                        </Badge>
                                                    )}
                                                </div>
                                                {space.description && (
                                                    <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">
                                                        {space.description}
                                                    </p>
                                                )}
                                            </div>
                                            <EntityLockButton
                                                entityType="space"
                                                entityId={space.id}
                                                isLocked={isSpaceLocked(space)}
                                                workspaceId={currentWorkspaceId ?? undefined}
                                            />
                                            <DropdownMenu>
                                                <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                                                        data-testid={`button-space-menu-${space.id}`}
                                                    >
                                                        <MoreVertical className="h-3.5 w-3.5"/>
                                                    </Button>
                                                </DropdownMenuTrigger>
                                                <DropdownMenuContent align="end">
                                                    {renderSpaceOverflowMenu(space, "grid")}
                                                </DropdownMenuContent>
                                            </DropdownMenu>
                                        </div>
                                        {/* DS EDIT 5aug26 — goal/source/milestone counters removed from cards (operator markup) */}
                                    </CardContent>
                                </Card>
                            );
                        })}
                    </div>
                ) : (
                    <div className="space-y-1">
                        {filteredAndSortedSpaces.map((space) => {
                            const IconComponent = getIconComponent(space.icon || "Brain");

                            return (
                                <div
                                    key={space.id}
                                    className="px-3 py-2 rounded-md border hover-elevate cursor-pointer group"
                                    data-testid={`list-space-${space.id}`}
                                    onClick={() => setLocation(`/spaces/${space.id}`)}
                                >
                                    <div className="flex items-center gap-3">
                                        {isSpaceLocked(space) ? (
                                            <div className="p-1.5 rounded-md flex-shrink-0 bg-amber-500/10">
                                                <Lock className="h-4 w-4 text-amber-500"/>
                                            </div>
                                        ) : (
                                            <div
                                                className="p-1.5 rounded-md flex-shrink-0"
                                                style={{backgroundColor: `${space.color || "hsl(220, 70%, 50%)"}15`}}
                                            >
                                                <IconComponent
                                                    className="h-4 w-4"
                                                    style={{color: space.color || "hsl(220, 70%, 50%)"}}
                                                />
                                            </div>
                                        )}
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-3">
                                                <h3 className="font-medium text-sm">{space.name}</h3>
                                                {(space as SpaceListExtra).isShared && (
                                                    <Badge variant="secondary"
                                                           className="text-[10px] px-1.5 py-0 h-4 shrink-0">
                                                        Shared
                                                    </Badge>
                                                )}
                                                {(space as SpaceListExtra).isShared &&
                                                    (space as SpaceListExtra).sharedMemberRole === "editor" && (
                                                    <Badge variant="outline"
                                                           className="text-[10px] px-1.5 py-0 h-4 shrink-0 border-primary/40 text-primary">
                                                        Can edit
                                                    </Badge>
                                                )}
                                                {isSpaceLocked(space) && (
                                                    <Badge variant="outline"
                                                           className="text-[10px] px-1.5 py-0 h-4 shrink-0 text-amber-500 border-amber-500/30">
                                                        Locked
                                                    </Badge>
                                                )}
                                                {/* DS EDIT 5aug26 — goal/source/milestone counters removed from rows (operator markup) */}
                                            </div>
                                            {space.description && (
                                                <p className="text-xs text-muted-foreground line-clamp-1">
                                                    {space.description}
                                                </p>
                                            )}
                                        </div>
                                        <EntityLockButton
                                            entityType="space"
                                            entityId={space.id}
                                            isLocked={isSpaceLocked(space)}
                                            workspaceId={currentWorkspaceId ?? undefined}
                                        />
                                        <DropdownMenu>
                                            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                                                    data-testid={`button-list-space-menu-${space.id}`}
                                                >
                                                    <MoreVertical className="h-4 w-4"/>
                                                </Button>
                                            </DropdownMenuTrigger>
                                            <DropdownMenuContent align="end">
                                                {renderSpaceOverflowMenu(space, "list")}
                                            </DropdownMenuContent>
                                        </DropdownMenu>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Merge Space Dialog */}
            <AlertDialog
                open={!!spaceToMerge}
                onOpenChange={(open) => {
                    if (!open) {
                        setSpaceToMerge(null);
                        setTargetMergeSpaceId("");
                    }
                }}
            >
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2">
                            <Merge className="h-5 w-5"/>
                            Merge Space Into Another
                        </AlertDialogTitle>
                        <AlertDialogDescription asChild>
                            <div className="space-y-4">
                                <p>
                                    Select a target space to merge <strong>"{spaceToMerge?.name}"</strong> into.
                                    All topics, threads, library documents, linked sources, goals, and notes will be transferred.
                                </p>
                                <div className="bg-muted/50 border rounded-md p-3 space-y-2">
                                    <p className="font-medium text-sm">What will happen:</p>
                                    <ul className="text-sm space-y-1 ml-4 list-disc text-muted-foreground">
                                        <li>Topics will be moved to the target space</li>
                                        <li>Threads will be reassigned to the target space</li>
                                        <li>Library documents and source links will move to the target space</li>
                                        <li>Goals and space-level source lists will be merged</li>
                                        <li>Notes will be combined</li>
                                        <li>
                                            &quot;{spaceToMerge?.name}&quot; will be archived (soft-deleted) for 72 hours, then
                                            removed; you can restore from this page during that window
                                        </li>
                                    </ul>
                                </div>
                                <div className="space-y-2">
                                    <Label>Select Target Space:</Label>
                                    {mergeDestinationOptions.length === 0 ? (
                                        <p className="text-sm text-muted-foreground py-2">
                                            No other spaces available to merge into. Create another space first.
                                        </p>
                                    ) : (
                                        <ComboboxSelector
                                            options={mergeDestinationOptions}
                                            value={targetMergeSpaceId}
                                            onValueChange={setTargetMergeSpaceId}
                                            placeholder="Choose a space to merge into..."
                                            searchPlaceholder="Search spaces..."
                                            emptyText="No space matches your search."
                                            allowNone={false}
                                            data-testid="select-merge-target-space"
                                            className="w-[min(100vw-3rem,28rem)]"
                                        />
                                    )}
                                </div>
                            </div>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="button-cancel-merge">Cancel</AlertDialogCancel>
                        <Button
                            onClick={confirmMerge}
                            disabled={!targetMergeSpaceId || mergeSpaceMutation.isPending}
                            data-testid="button-confirm-merge"
                        >
                            {mergeSpaceMutation.isPending ? "Merging..." : "Merge Spaces"}
                        </Button>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Transfer Space Dialog */}
            <AlertDialog open={!!spaceToTransfer} onOpenChange={(open) => !open && setSpaceToTransfer(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2">
                            <ArrowRightLeft className="h-5 w-5" />
                            Transfer space
                        </AlertDialogTitle>
                        <AlertDialogDescription asChild>
                            <div className="space-y-4">
                                <p>
                                    Move <strong>"{spaceToTransfer?.name}"</strong> and all linked topics, threads, and documents
                                    to another workspace{currentWorkspaceId ? " or back to Personal" : ""}.
                                </p>
                                <div className="bg-muted/50 border rounded-md p-3 space-y-2">
                                    <p className="font-medium text-sm">Hard move behavior:</p>
                                    <ul className="text-sm space-y-1 ml-4 list-disc text-muted-foreground">
                                        <li>The space will leave this context and appear only under the destination you pick</li>
                                        <li>Topics, threads, docs, and source links are reassigned to match that destination</li>
                                        <li>You can keep share links active or revoke them during transfer</li>
                                    </ul>
                                </div>
                                <div className="space-y-2">
                                    <Label>Destination</Label>
                                    {(() => {
                                        const otherWorkspaces = (availableWorkspaces || []).filter(
                                            (w: { id: string }) => w.id !== currentWorkspaceId
                                        );
                                        const showPersonal =
                                            currentWorkspaceId != null && currentWorkspaceId !== "";
                                        const hasOptions = showPersonal || otherWorkspaces.length > 0;
                                        if (!hasOptions) {
                                            return (
                                                <p className="text-sm text-muted-foreground py-2">
                                                    No destination available. Create a workspace first to move spaces out of Personal.
                                                </p>
                                            );
                                        }
                                        return (
                                            <Select value={targetTransferWorkspaceId} onValueChange={setTargetTransferWorkspaceId}>
                                                <SelectTrigger data-testid="select-transfer-target-workspace">
                                                    <SelectValue placeholder="Choose destination..." />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {showPersonal && (
                                                        <SelectItem value={TRANSFER_TARGET_PERSONAL} data-testid="select-transfer-personal">
                                                            Personal
                                                        </SelectItem>
                                                    )}
                                                    {otherWorkspaces.map((workspace: { id: string; name: string }) => (
                                                        <SelectItem key={workspace.id} value={workspace.id}>
                                                            {workspace.name}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        );
                                    })()}
                                </div>
                                <div className="flex items-center gap-2">
                                    <Checkbox
                                        checked={preserveTransferShares}
                                        onCheckedChange={(checked) => setPreserveTransferShares(checked === true)}
                                        data-testid="checkbox-transfer-preserve-shares"
                                    />
                                    <Label className="text-sm">Preserve existing share links</Label>
                                </div>
                            </div>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="button-cancel-transfer">Cancel</AlertDialogCancel>
                        <Button
                            onClick={confirmTransfer}
                            disabled={!targetTransferWorkspaceId || transferSpaceMutation.isPending}
                            data-testid="button-confirm-transfer"
                        >
                            {transferSpaceMutation.isPending ? "Transferring..." : "Transfer Space"}
                        </Button>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Delete Confirmation Dialog */}
            <AlertDialog open={!!spaceToDelete} onOpenChange={(open) => !open && setSpaceToDelete(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2 text-destructive">
                            <AlertTriangle className="h-5 w-5"/>
                            Delete Mastermind Space
                        </AlertDialogTitle>
                        <AlertDialogDescription asChild>
                            <div className="space-y-4">
                                <p>
                                    You are about to permanently delete <strong>"{spaceToDelete?.name}"</strong>. This
                                    action cannot be undone.
                                </p>
                                <div
                                    className="bg-destructive/10 border border-destructive/20 rounded-md p-3 space-y-2">
                                    <p className="font-medium text-destructive text-sm">This will permanently
                                        delete:</p>
                                    <ul className="text-sm space-y-1 ml-4 list-disc text-muted-foreground">
                                        <li>All chat threads and conversations in this space</li>
                                        <li>All linked sources and their content</li>
                                        <li>All documents and notes</li>
                                        <li>All goals, milestones, and progress data</li>
                                        <li>AI agent configurations for this space</li>
                                    </ul>
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="confirm-delete" className="text-sm">
                                        Type <strong>"{spaceToDelete?.name}"</strong> to confirm:
                                    </Label>
                                    <Input
                                        id="confirm-delete"
                                        data-testid="input-confirm-delete"
                                        value={deleteConfirmText}
                                        onChange={(e) => setDeleteConfirmText(e.target.value)}
                                        placeholder="Type space name to confirm"
                                        className="border-destructive/50 focus-visible:ring-destructive"
                                    />
                                </div>
                            </div>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel
                            onClick={() => setDeleteConfirmText("")}
                            data-testid="button-cancel-delete"
                        >
                            Cancel
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={confirmDelete}
                            disabled={deleteConfirmText !== spaceToDelete?.name || deleteSpaceMutation.isPending}
                            className="bg-destructive hover:bg-destructive/90"
                            data-testid="button-confirm-delete"
                        >
                            {deleteSpaceMutation.isPending ? "Deleting..." : "Delete Space"}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Share Space Dialog */}
            {spaceToShare && (
                <ShareSpaceDialog
                    open={shareDialogOpen}
                    onOpenChange={setShareDialogOpen}
                    spaceId={spaceToShare.id}
                    spaceName={spaceToShare.name}
                />
            )}
        </div>
    );
}
